/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import { View } from '@components/atoms'
import { Modal, ModalInfoType } from '@components/molecules'
import { LinearProgress } from '@mui/material'

import { useAppSelector, CartType, ApiGetDataStatus } from '@redux/index'
import { useNavigate } from 'react-router-dom'
import { SeasonProductType, SeasonProductPhaseType, ProductOptionType } from '@schemas'
import { Analytics, Decimal, formatAmount, CartQuote, CartLineKey } from '@utils'
import dayjs from 'dayjs'

import { mono, monoFont } from './monolith.tokens'
import { useDisplayCurrency, useGeoCountry } from './useDisplayCurrency'

interface PropsType {
	product: SeasonProductType
}

/**
 * 원화 화면의 한 줄. 사실 둘 — 부가세가 들어 있다, 카드로 원화 결제가 된다(PayVerse). 배지·"Save"·
 * 해외가 대비 문구는 없다 (PRICING §7.4 규칙 3 · PERSONA §1). 머리줄 아래 11px 키커 자리에 선다.
 */
const KRW_NOTE = 'VAT included · Card payment in KRW'

/** 옵션 하나의 선택 — 옵션별로 수량이 따로 간다 */
interface SelectionType {
	option: ProductOptionType
	qty: number
}

/**
 * 구매 패널 — 가격 · 옵션 · 수량 · Add to cart.
 *
 * **옵션이 있는 제품은 옵션별 선택 행으로 산다** (2026-09-17, 네이버 스토어 문법). 칩을 누르면 그 옵션이
 * 수량 1 로 아래 행에 들어오고, 행마다 수량·줄 금액·Remove 가 있다. 총액은 행들의 합. 담으면 행 하나가
 * 장바구니 줄 하나가 된다(줄 = 시즌상품 × 옵션, `CartLineKey`). 옵션 추가금은 칩과 행에 그대로 보인다 —
 * 전에는 칩에 이름만 있고 총액이 추가금을 무시해서 고객이 추가금을 장바구니에서 처음 봤다.
 * 옵션이 없는 제품은 수량 하나·총액 하나 그대로다.
 *
 * 구 디자인 `molecules/shop/ShopOutline` 의 **거래 로직을 그대로 옮긴 것**이다(localStorage `cart`
 * 병합, 재고 상한, 옵션 선택, 담은 뒤 모달). 계산식과 저장 형식은 한 줄도 바꾸지 않았다 — 바꾼 것은
 * 껍데기뿐이다. ShopOutline 은 아직 구 페이지가 쓰고 있어 남겨 둔다.
 *
 * 외형은 DESIGN_SYSTEM §5 — 헤어라인·밑줄 입력·블랙 CTA 하나. MUI Button/TextField 외형을 쓰지 않는다.
 */
export const MonolithBuyPanel = (props: PropsType) => {
	// hook
	const navigate = useNavigate()

	// redux state
	const pageStatus: ApiGetDataStatus = useAppSelector(state => state.shop.shopItemStatus)
	const optionList: ProductOptionType[] = useAppSelector(state => state.shop.optionList)

	const hasOptions: boolean = optionList.length > 0

	// state
	const [qty, setQty] = React.useState<number>(1)
	/** 옵션 제품의 선택 행. 기본 선택은 없다 — 추가금이 붙는 옵션을 말없이 골라 두지 않는다 */
	const [selections, setSelections] = React.useState<SelectionType[]>([])
	const [showModal, setShowModal] = React.useState<boolean>(false)
	const [modalInfo, setModalInfo] = React.useState<ModalInfoType>({
		target: '',
		title: '',
	})
	// 제품(옵션 목록)이 바뀌면 선택을 비운다
	React.useEffect(() => setSelections([]), [props.product.id])

	/*
		지금 유효한 값. 판매 단계(얼리버드·프리오더·재시작)가 잡혀 있으면 그 구간의 값이고,
		단계를 안 넣었으면 서버가 시즌단가를 그대로 돌려준다. 표시·합계·장바구니가 전부
		이 값을 쓰며, 서버의 결제 금액 재계산(Payment.service getPaidUsd)도 같은 기준이다
		(CATALOG_PLAN §7.4).
	*/
	const effectiveUsd: number =
		props.product.phaseUsd > 0 ? props.product.phaseUsd : props.product.seasonUsd

	/*
		같은 자리의 원화(부가세 포함, PRICING §7). 한국 방문자에게는 **이 값만** 보인다 — 달러를 작게라도
		같이 두지 않는다(§7.3, 2026-09-16 사용자 결정). 장바구니·견적·결제 계산은 그대로 USD 기준이고
		(`unitKrw` 는 이미 담고 있었다), 여기서 바뀌는 것은 표시뿐이다. 원화가 없으면 USD 로 되돌린다.
	*/
	const effectiveKrw: number =
		props.product.phaseKrw > 0 ? props.product.phaseKrw : props.product.seasonKrw
	const currency = useDisplayCurrency()
	const showKrw: boolean = currency === 'KRW' && effectiveKrw > 0

	/** 옵션 추가금(표시 통화). `EXTRA_USD/KRW` 는 문자열로 온다 */
	const extraOf = (anOption: ProductOptionType): number =>
		showKrw ? Number(anOption.extraKrw) || 0 : Number(anOption.extraUsd) || 0
	/** 옵션 포함 단가(표시 통화) */
	const unitOf = (anOption: ProductOptionType): number =>
		showKrw
			? Math.round(effectiveKrw + extraOf(anOption))
			: Decimal(effectiveUsd + extraOf(anOption))
	const selectedQty: number = selections.reduce((sum, aSel) => sum + aSel.qty, 0)
	const selectedTotal: number = selections.reduce(
		(sum, aSel) => sum + unitOf(aSel.option) * aSel.qty,
		0,
	)

	/*
		줄 긋는 값은 시즌의 "할인 전 가격"(seasonDiscountUsd)이다. 기준단가(standardUsd)는
		shop 에 나오지 않는 참고값 (PRICING §5, 2026-09-15). 판매가보다 높을 때만 긋는다.
	*/
	const isDiscounted = props.product.seasonDiscountUsd > effectiveUsd

	const subtotalUsd = React.useMemo(
		() => Decimal(effectiveUsd * qty),
		[props.product, qty],
	)
	const priceExUsd: number = React.useMemo(() => Decimal(subtotalUsd), [subtotalUsd])
	const vatUsd: number = React.useMemo(
		() => Decimal(subtotalUsd - priceExUsd),
		[subtotalUsd, priceExUsd],
	)

	/** 이미 장바구니에 담아 둔 수량. 재고 상한에서 빼야 한다. */
	const localQty: number = React.useMemo(() => {
		const localeCartSrt = localStorage.getItem('cart')
		const tempItemList: CartType[] =
			localeCartSrt === null || localeCartSrt === '' ? [] : JSON.parse(localeCartSrt)
		// 옵션별 줄이 여러 개일 수 있다 — 같은 제품의 줄을 전부 합친다
		return tempItemList
			.filter(anItem => anItem.id === props.product.id)
			.reduce((sum, anItem) => sum + (anItem.qty ?? 0), 0)
	}, [props.product])

	const status: string = React.useMemo(
		() =>
			props.product.seasonQuantity - props.product.soldQuantity - localQty === 0
				? 'sold_out'
				: props.product.status,
		[props.product, localQty],
	)

	/**
	 * 고객 화면에 "시즌"은 내부 용어라 노출하지 않는다 — 상태만 말한다.
	 * Shop 목록(`monolith/Shop.page.tsx`)의 라벨과 같은 값을 쓴다.
	 */
	const stockLabel: string =
		props.product.status === 'preorder_closed'
			? 'Pre-order sold out'
			: props.product.status === 'preorder_not_started'
			? 'Pre-order not open'
			: props.product.status === 'active' && props.product.preorderYn === 1
			? 'Pre-order'
			: status === 'active'
			? 'In stock'
			: status === 'sold_out'
			? 'Sold out'
			: status === 'not_start'
			? 'Not yet open'
			: 'Unavailable'

	/**
	 * 아직 값이 안 매겨진 제품 — 살 수 없다. Shop 목록(`Shop.page.tsx`)의 `notOpen` 과 같은
	 * 판정이되 제품 코드에 매지 않았다: 가격이 0 이면 어느 제품이든 담을 수 없다.
	 * 상품이 아직 안 들어온 상태(`id === ''`)와 구분해야 $0.00 이 잠깐 비치지 않는다.
	 */
	const isLoaded = props.product.id !== ''
	const notPriced = isLoaded && effectiveUsd <= 0

	/*
		프리오더 화면의 값 (2026-09-15 가격 구조 결정 — 얼리버드 $799 · 프리오더 $899 · 판매가 $1,000).

		읽는 순서가 값을 결정한다. 처음 본 숫자로 제품 등급이 매겨지므로 **판매가가 먼저**
		읽혀야 한다 — 그래야 "천 달러짜리를 지금 799 에" 가 되고, 반대면 "8백 달러대 스위처" 가 된다.
		그래서 머리줄은 [줄 그은 판매가] [단계 값] 순이다(취소선 + 9 로 끝나는 값 = 할인의 시각 문법.
		배지·문장은 붙이지 않는다, PERSONA §1). 그 아래 표는 **다음에 올 값**만 적는다 — 기다리면
		잃는 금액이 정확히 $100 씩이라는 사실이 곧 일정이다. 지난 단계는 세우지 않는다.
	*/
	const salePhases = React.useMemo(
		() => (props.product.phases ?? []).filter(aPhase => aPhase.kind === 'sale'),
		[props.product.phases],
	)
	const findPhase = (name: string) => salePhases.find(aPhase => aPhase.phase === name)
	const earlybird = findPhase('earlybird')
	const preorder = findPhase('preorder')
	const restart = findPhase('restart')

	/** 프리오더 계단을 그릴 수 있는가 — 프리오더 단계가 값과 함께 잡혀 있어야 한다 */
	const hasPreorderLadder: boolean =
		props.product.preorderYn === 1 && preorder !== undefined && preorder.usd > 0

	/*
		프리오더가 끝나고 재시작 판매가 열릴 때까지 **살 수 없는 구간**이 있는가.
		그 구간은 저장하지 않는다 — 프리오더 종료와 재시작 시작 사이가 비어 있으면 그것이
		공백이다 (CATALOG_PLAN §7.3). 살 수 없는 날이 온다는 사실은 미리 적는다.
	*/
	const hasSaleGap: boolean =
		preorder !== undefined &&
		!!preorder.endAt &&
		restart !== undefined &&
		!!restart.startAt &&
		dayjs(restart.startAt).valueOf() - dayjs(preorder.endAt).valueOf() > 60 * 1000

	/** 1초마다 다시 그린다. 카운트다운이 멈춰 보이면 신뢰를 잃는다. */
	const [now, setNow] = React.useState<number>(() => Date.now())
	React.useEffect(() => {
		if (!hasPreorderLadder) return undefined
		const timer = window.setInterval(() => setNow(Date.now()), 1000)
		return () => window.clearInterval(timer)
	}, [hasPreorderLadder])

	/*
		남은 시간을 영어 축약으로. 하루 이상이면 분·초는 버린다 — 읽는 쪽이 필요 없다.
		**5시간 안쪽부터 초를 센다** — 마감이 눈앞일 때만 초가 정보가 된다. 분·초는 두 자리로
		고정한다. `9s → 10s` 에서 자릿수가 바뀌면 옆 라벨이 흔들린다(값은 tabular-nums).
	*/
	const SECONDS_FROM = 5 * 3600

	const remaining = (target: string): string => {
		const diff = dayjs(target).valueOf() - now
		if (diff <= 0) return ''
		const sec = Math.floor(diff / 1000)
		const d = Math.floor(sec / 86400)
		const h = Math.floor((sec % 86400) / 3600)
		const m = Math.floor((sec % 3600) / 60)
		const sc = sec % 60
		const pad = (aValue: number): string => String(aValue).padStart(2, '0')
		if (d >= 1) return h > 0 ? `${d}d ${h}h` : `${d}d`
		if (sec > SECONDS_FROM) return `${h}h ${m}m`
		if (h >= 1) return `${h}h ${pad(m)}m ${pad(sc)}s`
		if (m >= 1) return `${m}m ${pad(sc)}s`
		return `${sc}s`
	}

	const phaseRange = (aPhase: any): string => {
		if (aPhase === undefined || !aPhase.startAt) return ''
		const from = dayjs(aPhase.startAt).format('MMM D')
		const to = aPhase.endAt ? dayjs(aPhase.endAt).format('MMM D') : ''
		return to === '' ? `From ${from}` : `${from} – ${to}`
	}

	const inRange = (aPhase: any): boolean =>
		aPhase !== undefined &&
		!!aPhase.startAt &&
		now >= dayjs(aPhase.startAt).valueOf() &&
		(!aPhase.endAt || now <= dayjs(aPhase.endAt).valueOf())

	/*
		주연으로 세울 단계. **가장 싼 값이 아니라 지금(또는 다음에) 살 수 있는 값**이다 —
		얼리버드가 끝난 뒤에도 $800 을 크게 두면 살 수 없는 값을 내세우게 된다.
	*/
	const leadPhase = React.useMemo(() => {
		if (!hasPreorderLadder) return undefined
		if (inRange(earlybird)) return earlybird
		if (inRange(preorder)) return preorder
		const upcoming = [earlybird, preorder]
			.filter(
				(aPhase): aPhase is SeasonProductPhaseType =>
					aPhase !== undefined && !!aPhase.startAt,
			)
			.filter(aPhase => now < dayjs(aPhase.startAt).valueOf())
			.sort((a, b) => dayjs(a.startAt).valueOf() - dayjs(b.startAt).valueOf())
		return upcoming.length > 0 ? upcoming[0] : undefined
	}, [hasPreorderLadder, earlybird, preorder, now])

	const leadName: string =
		leadPhase === undefined
			? ''
			: leadPhase.phase === 'earlybird'
			? 'Early bird'
			: 'Pre-order'

	/*
		값 계단 — 얼리버드 · 프리오더 · 판매가를 시간순으로 한 줄에 세운다. 표로 쌓지 않고
		점과 선으로 잇는다 — 순서가 곧 정보라 "지금 어디이고 다음이 얼마인가" 가 한 번에 읽힌다.
		지난 단계도 자리를 지킨다: 없애면 남은 값이 원래 그 값이었던 것처럼 읽힌다.
		판매가 칸은 기간 없이 값만 적는다 (2026-09-21 사용자 결정).
	*/
	const stepRows = React.useMemo(() => {
		if (!hasPreorderLadder) return []

		const stateOf = (aPhase?: SeasonProductPhaseType): 'past' | 'now' | 'next' => {
			if (aPhase === undefined || !aPhase.startAt) return 'next'
			if (inRange(aPhase)) return 'now'
			return now > dayjs(aPhase.startAt).valueOf() ? 'past' : 'next'
		}
		const amountOf = (aPhase: SeasonProductPhaseType): number =>
			showKrw ? aPhase.krw : aPhase.usd

		const rows: {
			key: string
			label: string
			range: string
			amount: number
			state: 'past' | 'now' | 'next'
			/** 다음 칸으로 가는 선을 점선으로 — 그 사이에 못 사는 구간이 있다 */
			dashed: boolean
		}[] = []

		if (earlybird !== undefined && earlybird.usd > 0) {
			rows.push({
				key: 'earlybird',
				label: 'Early bird',
				range: phaseRange(earlybird),
				amount: amountOf(earlybird),
				state: stateOf(earlybird),
				dashed: false,
			})
		}
		if (preorder !== undefined && preorder.usd > 0) {
			rows.push({
				key: 'preorder',
				label: 'Pre-order',
				range: phaseRange(preorder),
				amount: amountOf(preorder),
				state: stateOf(preorder),
				dashed: hasSaleGap,
			})
		}
		rows.push({
			key: 'list',
			label: 'List price',
			range: '',
			amount: showKrw ? props.product.seasonKrw : props.product.seasonUsd,
			state: restart === undefined ? 'next' : stateOf(restart),
			dashed: false,
		})
		return rows
	}, [
		hasPreorderLadder,
		earlybird,
		preorder,
		restart,
		hasSaleGap,
		showKrw,
		props.product,
		now,
	])

	/*
		카운트다운은 **문장이 아니라 값과 라벨**이다. 문장은 읽어야 보이지만 큰 숫자는
		읽지 않아도 보인다. 한 번에 하나만 센다 (PERSONA §1 — 설명하지 않고 사실만 놓는다).
	*/
	const countdown: { value: string; label: string } | undefined = React.useMemo(() => {
		if (leadPhase === undefined) return undefined

		if (inRange(leadPhase)) {
			if (!leadPhase.endAt) return undefined
			const left = remaining(leadPhase.endAt)
			return left === '' ? undefined : { value: left, label: `${leadName} ends` }
		}
		const left = remaining(leadPhase.startAt)
		return left === ''
			? undefined
			: { value: left, label: `Until ${leadName.toLowerCase()}` }
	}, [leadPhase, leadName, now])

	/*
		이번에 푸는 수량. **재고가 아니라 한도**다 — 걸어 둔 수량의 절반만 이번 프리오더에 푼다.
		남은 개수를 세는 자리가 아니므로 판매량을 빼지 않는다.
	*/
	const limitedCount: number = Math.floor((props.product.seasonQuantity ?? 0) / 2)

	/*
		단계를 정해 뒀는데 아직 그 구간이 아니다. `notPriced` 와 **묶으면 안 된다** —
		값이 없는 게 아니라 값은 정해져 있고 아직 안 열렸을 뿐이라 "Pricing is not set" 은
		사실이 아니다. 다만 지금 살 수 없는 값을 크게 내세우지는 않는다.
	*/
	const preorderNotStarted = props.product.status === 'preorder_not_started'

	/*
		이 값이 끝나는 길은 둘이다 — 날짜가 오거나, 이번 배치가 다 나가거나.
		물량이 먼저 떨어지면 다음 단계로 넘어가지 않고 그 자리에서 판매가 끝난다.
		둘 다 화면에 있어야 한다(PRICING §2 규칙 4). 다만 날짜는 값 계단과 카운트다운이
		이미 두 번 말하므로 이 줄은 **배치만** 말한다 — 2026-09-21 이전에는 날짜를 세 번째로 반복했다.
	*/
	const batchCloseLine: string =
		leadPhase === undefined || !leadPhase.endAt || !inRange(leadPhase)
			? ''
			: `The ${leadName.toLowerCase()} price also ends when this batch is gone.`

	/** 가격표 문법 — `$1,000` · `₩1,199,000`, `.00` 없음 (`utils/Currency.ts`) */
	const money = (amount: number): string => formatAmount(showKrw ? 'KRW' : 'USD', amount)
	/*
		착지 원가 — TOTAL 블록 안, 금액과 같은 열에 걸리는 한 줄짜리 키커 두세 개
		(PRICING §8.4, 2026-09-21 사용자 결정). 이 금액이 무엇을 포함하는지 말하는 줄이라 금액에 붙인다.
		라벨과 값을 좌·우로 벌리지 않고 **한 구로 붙여 읽는다** — `SHIPPING & VAT INCLUDED`.
		끝맺는 말은 `included` · `on delivery` · `at checkout` 뿐이고, 포함되는 것끼리 묶어 줄 수를 줄였다. 말하는 사실은 전과 같다 — 관세는 우리가 낸다(DDP) / 소비세는
		각자 자기 나라 것을 낸다. "duties included" 만 쓰면 유럽 고객은 "문 앞에서 낼 게 없다" 로
		읽고 VAT 청구서에 속았다고 느끼므로 `VAT OR GST` 줄을 세운다.

		**미국 방문자에게는 그 줄을 감춘다** (PRICING §8.3 예외, 2026-09-21 사용자 결정). 미국은
		소비세가 없고 관세는 우리가 내므로 문 앞에서 낼 것이 0 이다 — `On delivery` 는 미국에서
		거짓이다. 감추는 조건은 **`US` 로 확정됐을 때뿐**이다: 판정 전·판정 실패(`UNKNOWN`)·
		프리렌더는 전부 노출이라, 오판정은 "유럽 고객이 VAT 고지를 못 받는" 쪽이 아니라
		"미국 고객이 해당 없는 줄을 한 줄 더 본다" 쪽으로만 넘어진다. 색인 정본(프리렌더)도 노출본이다.

		배송비는 서버 규칙(소계 $100 미만 $20, KR 0) — 액세서리만 `SHIPPING · At checkout`.
		이 블록이 서면서 CTA 아래 키커는 걷었다. 같은 사실을 두 번 말하지 않는다.
	*/
	const isUs: boolean = useGeoCountry() === 'US'

	const landedLines: { key: string; text: string }[] = showKrw
		? [{ key: 'inc', text: 'Shipping & VAT included' }]
		: [
				...(effectiveUsd >= 100
					? [{ key: 'inc', text: 'Shipping & duties included' }]
					: [
							{ key: 'duty', text: 'Duties included' },
							{ key: 'ship', text: 'Shipping at checkout' },
					  ]),
				...(isUs ? [] : [{ key: 'tax', text: 'VAT or GST on delivery' }]),
		  ]

	/** 단계 값의 표시 통화 자리 — 원화 화면이면 `krw`, 아니면 `usd` */
	const phaseAmount = (aPhase: SeasonProductPhaseType | undefined): number =>
		aPhase === undefined ? 0 : showKrw ? aPhase.krw : aPhase.usd
	const listAmount: number = showKrw ? props.product.seasonKrw : props.product.seasonUsd

	const onQtyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const str = e.target.value

		const localeCartSrt = localStorage.getItem('cart')
		const tempItemList: CartType[] =
			localeCartSrt === null || localeCartSrt === '' ? [] : JSON.parse(localeCartSrt)
		const inCartQty: number = tempItemList
			.filter(anItem => anItem.id === props.product.id)
			.reduce((sum, anItem) => sum + (anItem.qty ?? 0), 0)

		const room = props.product.seasonQuantity - props.product.soldQuantity - inCartQty
		const typedQty = Number(str) > room ? room : Number(str)

		setQty(typedQty < 1 ? 1 : typedQty)
	}

	/** 재고 상한 — 시즌 수량에서 판매분과 이미 담은 분을 뺀 것. 옵션 행들이 이 안에서 나눠 쓴다 */
	const room: number =
		props.product.seasonQuantity - props.product.soldQuantity - localQty

	/** 칩 클릭 — 없던 옵션이면 수량 1 로 행을 만든다. 이미 있으면 그대로(행에서 수량을 만진다) */
	const onOptionClick = (anOption: ProductOptionType) => {
		if (selections.some(aSel => aSel.option.id === anOption.id)) return
		if (room - selectedQty < 1) return
		setSelections(prev => [...prev, { option: anOption, qty: 1 }])
	}

	const onSelectionQtyChange = (optionId: string, str: string) => {
		const others = selections
			.filter(aSel => aSel.option.id !== optionId)
			.reduce((sum, aSel) => sum + aSel.qty, 0)
		const cap = Math.max(1, room - others)
		const typedQty = Number(str)
		const nextQty = isNaN(typedQty) || typedQty < 1 ? 1 : typedQty > cap ? cap : typedQty
		setSelections(prev =>
			prev.map(aSel => (aSel.option.id === optionId ? { ...aSel, qty: nextQty } : aSel)),
		)
	}

	const onSelectionRemove = (optionId: string) => {
		setSelections(prev => prev.filter(aSel => aSel.option.id !== optionId))
	}

	/** 장바구니 줄 하나. `seasonUsd`·`unitKrw` 는 옵션 제외 단가(서버 unitSeen 비교값), `sumUsd` 는 옵션 포함 */
	const buildCartItem = (lineQty: number, anOption?: ProductOptionType): CartType => {
		const optionUsd = anOption === undefined ? 0 : Number(anOption.extraUsd) || 0
		const newCartItem: CartType = {
			id: props.product.id,
			seasonId: props.product.seasonId,
			productId: props.product.productId,
			productCode: props.product.productCode,
			seasonUsd: effectiveUsd,
			// 원화 단가(부가세 포함)와 단계도 함께 담는다 — 서버 견적이 "값이 바뀌었나"를 이 값으로 본다
			unitKrw:
				props.product.phaseKrw > 0 ? props.product.phaseKrw : props.product.seasonKrw,
			phase: props.product.currentPhase,
			seasonDiscountUsd: props.product.seasonDiscountUsd,
			productName: props.product.productName,
			standardUsd: props.product.standardUsd,
			seasonQuantity: props.product.seasonQuantity,
			soldQuantity: props.product.soldQuantity,
			qty: lineQty,
			sumUsd: Decimal((effectiveUsd + optionUsd) * lineQty),
			vatUsd: 0,
		}
		if (anOption !== undefined && anOption.id !== '') {
			newCartItem.option = { ...anOption }
		}
		return newCartItem
	}

	const onAddToCartClick = () => {
		const newItems: CartType[] = hasOptions
			? selections.map(aSel => buildCartItem(aSel.qty, aSel.option))
			: [buildCartItem(qty)]

		if (newItems.length === 0) return

		const localeCartList = localStorage.getItem('cart')
		const parsed =
			localeCartList === null || localeCartList === '' ? [] : JSON.parse(localeCartList)
		const tempCartList: CartType[] = Array.isArray(parsed) ? parsed : []

		// 같은 줄(시즌상품 × 옵션)이 이미 있으면 수량을 합친다
		const newCartList: CartType[] = [...tempCartList]
		for (const anItem of newItems) {
			const idx = newCartList.findIndex(aLine =>
				CartLineKey.is(aLine, CartLineKey.of(anItem)),
			)
			if (idx === -1) {
				newCartList.push(anItem)
			} else {
				const nextQty = (newCartList[idx].qty ?? 0) + anItem.qty
				newCartList[idx] = {
					...newCartList[idx],
					qty: nextQty,
					sumUsd: Decimal(CartQuote.localUnitUsd(newCartList[idx]) * nextQty),
				}
			}
		}

		localStorage.setItem('cart', JSON.stringify(newCartList))
		window.dispatchEvent(new Event('storage'))
		newItems.forEach(anItem => Analytics.addToCart(anItem))
		setSelections([])

		// 상시판매는 서버가 24시간 카트 홀드를 잡는다. 허락한 수량이 적으면 그만큼으로 (CHECKOUT_PLAN §11)
		void CartQuote.syncHolds(newCartList).then(({ cartList, reduced }) => {
			localStorage.setItem('cart', cartList.length === 0 ? '' : JSON.stringify(cartList))
			window.dispatchEvent(new Event('storage'))
			if (reduced.length > 0) {
				sessionStorage.setItem('cartNotices', JSON.stringify(reduced))
			}
		})

		setModalInfo({ target: 'addToCart', title: 'Add to Cart' })
		setShowModal(true)
	}

	const onModalSubmit = () => {
		if (modalInfo.target === 'addToCart') {
			navigate('/cart')
		}
	}

	return (
		<View css={style}>
			<View className="mnBuyHead">
				{/* 값이 없으면 가격 자리를 비워 둔다 — $0.00 을 찍지 않는다 */}
				{!notPriced && (leadPhase === undefined || !hasPreorderLadder) && (
					<View className="mnBuyPrice mnBuyPricePhase">
						<View className="mnBuyPriceLine">
							{/* 할인 전 원화는 DB 에 없다 — 줄은 USD 화면에서만 긋는다 */}
							{isDiscounted && !showKrw && (
								<span className="mnBuyPriceStruck">
									{money(props.product.seasonDiscountUsd)}
								</span>
							)}
							<span className="mnBuyPriceValue">
								{money(showKrw ? effectiveKrw : effectiveUsd)}
							</span>
						</View>
						{showKrw && <span className="mnBuyPhase">{KRW_NOTE}</span>}
					</View>
				)}
				{hasPreorderLadder && leadPhase !== undefined && (
					/*
						판매가가 왼쪽, 단계 값이 오른쪽 — 눈이 판매가를 먼저 지난다. 판매가는 앵커라
						읽히는 크기여야 한다(비교값처럼 지우듯 작게 쓰지 않는다). 단계 이름과 기간은
						바로 아래 값 계단이 말하므로 여기서는 값만 세운다 (2026-09-21) — 대문자 키커가
						머리줄 아래 두 줄씩 쌓여 화면이 시끄러웠다.
					*/
					<View className="mnBuyPrice mnBuyPricePhase">
						<View className="mnBuyPriceLine">
							{listAmount > phaseAmount(leadPhase) && (
								<span className="mnBuyPriceStruck">{money(listAmount)}</span>
							)}
							<span className="mnBuyPriceValue">{money(phaseAmount(leadPhase))}</span>
						</View>

						{/* 원화 화면에는 결제 사실 한 줄 — "원화로 결제된다" 를 작은 달러 대신 이 줄이 말한다 */}
						{showKrw && <span className="mnBuyPhase">{KRW_NOTE}</span>}
					</View>
				)}
				<span className="mnBuyStock">{notPriced ? 'Not yet open' : stockLabel}</span>
			</View>

			{hasPreorderLadder && leadPhase !== undefined && (
				<View className="mnBuyLadder">
					{/*
						값 계단 — 얼리버드 ▸ 프리오더 ▸ 판매가. 칸마다 상자 하나이고, 지금 살 수 있는
						칸만 검정으로 채워진다. 올 값에는 취소선을 긋지 않는다 — 취소선은 머리줄의 판매가 하나뿐이다.
					*/}
					{stepRows.length > 1 && (
						<View className="mnBuySteps" aria-label="Price schedule">
							{/*
								레일 — 점이 상자 가운데 위에 선다. 칸(`mnBuyStepTick`)과 이음(`mnBuyRailGap`)이
								아래 상자 줄과 같은 폭 규칙을 쓰므로 점과 상자 중심이 어긋나지 않는다.
								못 사는 구간이 걸친 마디만 점선이다.
							*/}
							<View className="mnBuyStepRail" aria-hidden={true}>
								{stepRows.map((aStep, idx) => {
									const before = idx === 0 ? 'off' : stepRows[idx - 1].dashed ? 'gap' : ''
									const after =
										idx === stepRows.length - 1 ? 'off' : aStep.dashed ? 'gap' : ''
									return (
										<React.Fragment key={aStep.key}>
											<span className="mnBuyStepTick">
												<span className={`mnBuyRailLine ${before}`} />
												<span className={`mnBuyStepDot is-${aStep.state}`} />
												<span className={`mnBuyRailLine ${after}`} />
											</span>
											{idx < stepRows.length - 1 && (
												<span className={`mnBuyRailGap ${after}`} />
											)}
										</React.Fragment>
									)
								})}
							</View>

							<View className="mnBuyStepBoxes">
								{stepRows.map(aStep => (
									<View key={aStep.key} className={`mnBuyStep is-${aStep.state}`}>
										<span className="mnBuyStepLabel">{aStep.label}</span>
										{/* 기간이 없는 칸도 자리를 비워 둔다 — 값 줄의 눈높이가 셋 다 같아야 한다 */}
										<span className="mnBuyStepRange">
											{aStep.range === '' ? '\u00A0' : aStep.range}
										</span>
										<span className="mnBuyStepValue">{money(aStep.amount)}</span>
									</View>
								))}
							</View>
						</View>
					)}

					{/*
						시간과 수량. 둘 다 "숫자 + 라벨" 이라 형태가 같아 나란히 두면 한 쌍으로 읽힌다.
						세로로 쌓으면 목록이 된다.
					*/}
					{(countdown !== undefined || limitedCount > 0) && (
						<View className="mnBuyMeters">
							{countdown !== undefined && (
								<View className="mnBuyMeter mnBuyMeterTime">
									<span className="mnBuyMeterValue">{countdown.value}</span>
									<span className="mnBuyMeterLabel">{countdown.label}</span>
								</View>
							)}
							{limitedCount > 0 && (
								<View className="mnBuyMeter mnBuyMeterLimit">
									<span className="mnBuyMeterValue">{limitedCount.toLocaleString()}</span>
									<span className="mnBuyMeterLabel">Limited to</span>
								</View>
							)}
						</View>
					)}

					{/*
						조건 두 줄은 한 덩어리로. 따로 띄우면 문장이 화면에 흩어져 읽힌다 —
						원래는 값 표와 미터 사이에 한 줄씩 끊겨 있었다.
					*/}
					{(batchCloseLine !== '' || hasSaleGap) && (
						<View className="mnBuyLadderNotes">
							{batchCloseLine !== '' && (
								<p className="mnBuyLadderNote">{batchCloseLine}</p>
							)}
							{hasSaleGap && (
								<p className="mnBuyLadderNote">
									{'We do not sell this between the pre-order and the list price.'}
								</p>
							)}
						</View>
					)}
				</View>
			)}
			{['init', 'loading'].includes(pageStatus) ? (
				<View className="mnBuyLoading">
					<LinearProgress className="mnBuyLoadingBar" />
				</View>
			) : notPriced ? (
				/*
					값이 정해지기 전. 없는 것을 예고하지 않고 사실만 적는다 (PERSONA §1).

					관심 등록은 받지 않는다. 예전에는 여기서 `/contact` 로 보내는 "Register
					interest" 버튼을 세웠는데, 그러면 제품 하나만 다른 경로로 빠진다 — 나머지
					넷은 이 화면에서 장바구니로 끝난다. 값이 들어오면 아래 구매 UI 가 그대로
					뜨므로 이 자리는 비워 둔다.
				*/
				<p className="mnBuyNote mnBuyNoteFirst">
					Pricing and the release date are not set.
				</p>
			) : (
				<>
					{hasOptions && (
						<View className="mnBuyField">
							<span className="mnBuyLabel">Option</span>
							{/*
								칩 = 옵션 이름 + 추가금. 누르면 아래 선택 행에 들어간다(라디오가 아니다 — 옵션마다
								따로 살 수 있다). 추가금이 0 이면 이름만.
							*/}
							<View className="mnBuyChips" aria-label="Option">
								{optionList.map(anOption => {
									const picked = selections.some(aSel => aSel.option.id === anOption.id)
									const extra = extraOf(anOption)
									return (
										<button
											key={anOption.id}
											type="button"
											aria-pressed={picked}
											className={`mnBuyChip ${picked ? 'on' : ''}`}
											disabled={status !== 'active'}
											onClick={() => onOptionClick(anOption)}
										>
											{anOption.name}
											{extra > 0 && (
												<span className="mnBuyChipExtra">{`+${money(extra)}`}</span>
											)}
										</button>
									)
								})}
							</View>
						</View>
					)}

					{status === 'active' && hasOptions && selections.length > 0 && (
						/*
							선택 행 — 옵션 이름 · 옵션 포함 단가 | 수량 | 줄 금액 | Remove. 수량은 옵션에 붙는다.
							행들이 재고 상한을 나눠 쓴다.
						*/
						<View className="mnBuySels" role="list" aria-label="Selected options">
							{selections.map(aSel => (
								<View key={aSel.option.id} className="mnBuySel" role="listitem">
									<View className="mnBuySelName">
										<span className="mnBuySelOption">{aSel.option.name}</span>
										<span className="mnBuySelUnit">{`${money(
											unitOf(aSel.option),
										)} each`}</span>
									</View>
									<View className="mnBuySelQtyField">
										<label
											className="mnBuyLabel"
											htmlFor={`mnBuySelQty-${aSel.option.id}`}
										>
											Qty
										</label>
										<input
											id={`mnBuySelQty-${aSel.option.id}`}
											className="mnBuyQty mnBuySelQty"
											type="number"
											min={1}
											value={aSel.qty}
											onChange={e => onSelectionQtyChange(aSel.option.id, e.target.value)}
										/>
									</View>
									<span className="mnBuySelAmount">
										{money(unitOf(aSel.option) * aSel.qty)}
									</span>
									<button
										type="button"
										className="mnBuySelRemove"
										onClick={() => onSelectionRemove(aSel.option.id)}
									>
										Remove
									</button>
								</View>
							))}
						</View>
					)}

					{status === 'active' && !hasOptions && (
						<View className="mnBuyField mnBuyQtyField">
							<label className="mnBuyLabel" htmlFor="mnBuyQty">
								Quantity
							</label>
							<input
								id="mnBuyQty"
								className="mnBuyQty"
								type="number"
								min={1}
								value={qty}
								onChange={onQtyChange}
							/>
						</View>
					)}

					{status === 'active' && (!hasOptions || selections.length > 0) && (
						<View className="mnBuyTotal">
							<span className="mnBuyLabel">Total</span>
							<span className="mnBuyTotalValue">
								{hasOptions
									? money(selectedTotal)
									: showKrw
									? money(Math.round(effectiveKrw * qty))
									: money(priceExUsd + vatUsd)}
							</span>

							{/*
								착지 원가는 **이 금액에 대한 설명**이다. 그래서 TOTAL 블록 밖에 두지 않고
								같은 그리드의 둘째 줄, 금액과 같은 열에 건다 — 왼쪽 끝이 숫자와 맞으면
								장식 없이도 숫자에 딸린 줄로 읽힌다 (DESIGN_SYSTEM §0).
							*/}
							<View className="mnBuyLanded">
								{landedLines.map(aLine => (
									<p key={aLine.key} className="mnBuyLandedLine">
										{aLine.text}
									</p>
								))}
							</View>
						</View>
					)}

					<View className="mnBuyAction">
						{status === 'active' ? (
							<button
								type="button"
								className="mnCta mnBuyCta"
								disabled={hasOptions && selections.length === 0}
								onClick={onAddToCartClick}
							>
								Add to cart
							</button>
						) : status === 'not_start' ? (
							<span className="mnBuyNote">
								{`Pre-order opens ${dayjs(props.product.seasonStartDate).format(
									'MMM D, YYYY',
								)}.`}
							</span>
						) : props.product.status === 'preorder_closed' ? (
							/*
								프리오더가 끝났고 재시작 판매는 아직 열리지 않았다. 잠긴 이유를
								그대로 적는다 — 없는 것을 예고하지 않는다 (PERSONA §1).
								**"It sold out" 이라고 적지 않는다** — 이 구간은 수량이 남아 있어도
								닫히므로(CATALOG_PLAN §7.3) 완판이 아닌 날에도 거짓말이 된다.
							*/
							<span className="mnBuyNote">
								{restart !== undefined && restart.startAt
									? `The pre-order is over. It returns at the list price on ${dayjs(
											restart.startAt,
									  ).format('MMM D')}.`
									: 'The pre-order is over. It returns at the list price.'}
							</span>
						) : (
							<button type="button" className="mnCta mnBuyCta" disabled={true}>
								Add to cart
							</button>
						)}
					</View>

					{status === 'active' && hasOptions && selections.length === 0 && (
						/* 왜 잠겼는지 한 줄 (PERSONA §1). 기본 선택을 두지 않는 대신 이 줄이 선다 */
						<p className="mnBuyNote">Choose an option.</p>
					)}

					{preorderNotStarted && leadPhase !== undefined && leadPhase.startAt && (
						/* 버튼은 자리에 두되 잠근다. 왜 잠겼는지는 한 줄로 적는다 (PERSONA §1). */
						<p className="mnBuyNote">
							{`Opens ${dayjs(leadPhase.startAt).format('MMM D')}.`}
						</p>
					)}

					{props.product.status === 'active' && props.product.preorderYn === 1 && (
						/*
							프리오더에서 가장 먼저 묻는 둘 — 언제 받나, 무르면 되나. 두 문장을 라벨 하나
							아래 묶는다(2026-09-21) — 따로 띄워 두면 CTA 아래에 문장이 흩어진다.
							출고 기한은 **한국에서 떠나는 날** 기준이다. 도착일은 나라마다 달라 약속할 수 없다.
							취소 조건은 스토어 정책과 같은 문장을 쓴다. "This is a pre-order." 는 빠졌다 —
							머리줄의 PRE-ORDER 라벨과 값 계단이 이미 말한다.
						*/
						<View className="mnBuyTerms">
							<span className="mnBuyTermsLabel">Pre-order</span>
							<View className="mnBuyTermsLines">
								<p className="mnBuyTermsLine">
									{'We ship from Korea within four weeks after it closes.'}
								</p>
								<p className="mnBuyTermsLine">
									{'You can cancel any time before it ships. We refund the full amount.'}
								</p>
							</View>
						</View>
					)}
				</>
			)}

			<Modal
				target={modalInfo.target}
				title={modalInfo.title}
				show={showModal}
				closeStatus="inherit"
				showSubmit={true}
				closeTitle="Stay Here"
				submitTitle="Move to Cart"
				onSubmit={onModalSubmit}
				onCloseModal={() => setShowModal(false)}
			>
				{modalInfo.target !== '' ? (
					<View css={{ width: '30rem', maxWidth: '100%' }}>
						<p
							style={{
								fontFamily: monoFont.sans,
								fontSize: 16,
								color: mono.ink,
							}}
						>
							Added to your cart.
						</p>
					</View>
				) : null}
			</Modal>
		</View>
	)
}

const style = css({
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'flex-start',
	fontFamily: monoFont.sans,

	/** 가격(좌)과 재고(우)를 한 줄에 마주 세우고 아래를 헤어라인으로 닫는다 */
	'.mnBuyHead': {
		width: '100%',
		display: 'flex',
		alignItems: 'baseline',
		justifyContent: 'space-between',
		gap: 24,
		paddingBottom: 24,
		borderBottom: `1px solid ${mono.line}`,
		flexWrap: 'wrap',
	},

	'.mnBuyPrice': {
		display: 'flex',
		alignItems: 'baseline',
		gap: 16,

		/*
			줄 그은 판매가 — 앵커다. 지우듯 작게 쓰지 않는다: 18px `dim`. 단계 값(40px 200)이
			그 옆에 서면 "천 달러짜리를 지금 얼마에" 로 읽힌다. 순서를 바꾸면 뜻이 바뀐다.
		*/
		'.mnBuyPriceStruck': {
			fontSize: 18,
			fontWeight: 300,
			color: mono.dim,
			textDecoration: 'line-through',
			fontVariantNumeric: 'tabular-nums',
		},
		'.mnBuyPriceValue': {
			fontSize: 'clamp(30px, 3vw, 40px)',
			fontWeight: 200,
			color: mono.ink,
			lineHeight: 1,
			letterSpacing: '-0.02em',
			fontVariantNumeric: 'tabular-nums',
		},

		/** 단계 값 머리줄 — 값 한 줄 + 그 아래 단계 이름 · 기간 */
		'&.mnBuyPricePhase': {
			flexDirection: 'column',
			alignItems: 'flex-start',
			gap: 12,
		},
		'.mnBuyPriceLine': {
			display: 'flex',
			alignItems: 'baseline',
			gap: 16,
		},
		'.mnBuyPhase': {
			fontSize: 11,
			letterSpacing: '.18em',
			textTransform: 'uppercase',
			color: mono.faint,
			fontWeight: 500,
			fontVariantNumeric: 'tabular-nums',
		},
	},

	'.mnBuyStock': {
		fontSize: 13,
		letterSpacing: '.18em',
		textTransform: 'uppercase',
		color: mono.faint,
		fontWeight: 500,
	},

	'.mnBuyLoading': {
		width: 240,
		marginTop: 40,
		'.mnBuyLoadingBar': {
			'& .MuiLinearProgress-bar': { background: mono.ink },
			background: mono.line,
		},
	},

	/** 라벨 = 키커보다 좁은 자간(.2em). monoForm `.mnLabel` 과 같은 값 */
	'.mnBuyLabel': {
		fontSize: 12,
		letterSpacing: '.2em',
		textTransform: 'uppercase',
		color: mono.faint,
		fontWeight: 500,
	},

	'.mnBuyField': {
		width: '100%',
		display: 'flex',
		flexDirection: 'column',
		gap: 14,
		marginTop: 40,
	},

	'.mnBuyChips': {
		display: 'flex',
		flexWrap: 'wrap',
		gap: 10,

		'.mnBuyChip': {
			padding: '10px 20px',
			fontFamily: monoFont.sans,
			fontSize: 13,
			fontWeight: 400,
			color: mono.faint,
			background: 'transparent',
			border: `1px solid ${mono.lineSoft}`,
			borderRadius: 999,
			cursor: 'pointer',
			transition: 'color 200ms ease-out, border-color 200ms ease-out',

			'&.on': {
				color: mono.ink,
				borderColor: mono.ink,
				fontWeight: 500,
			},
			'&:disabled': {
				cursor: 'default',
				opacity: 0.5,
			},

			/** 추가금 — 이름 뒤에 한 칸, 이름보다 한 단계 눌러 */
			'.mnBuyChipExtra': {
				marginLeft: 10,
				fontWeight: 300,
				color: mono.dim,
				fontVariantNumeric: 'tabular-nums',
			},
		},
	},

	/*
		선택 행 — 헤어라인 행. [이름 · 단가](나머지) [수량 72] [줄 금액 auto] [Remove auto].
		Cart 의 행과 같은 문법(밑줄 숫자 입력 · 텍스트 Remove)이라 두 화면이 같은 물건을 같은 말로 만진다.
	*/
	'.mnBuySels': {
		width: '100%',
		marginTop: 28,
		borderTop: `1px solid ${mono.line}`,
	},
	'.mnBuySel': {
		display: 'grid',
		gridTemplateColumns: 'minmax(0, 1fr) 72px auto auto',
		alignItems: 'end',
		columnGap: 20,
		padding: '16px 0',
		borderBottom: `1px solid ${mono.line}`,

		'@media (max-width: 480px)': {
			gridTemplateColumns: 'minmax(0, 1fr) 64px auto',
			rowGap: 12,
			'.mnBuySelRemove': { gridColumn: '1 / -1', justifySelf: 'start' },
		},
	},
	'.mnBuySelName': {
		display: 'flex',
		flexDirection: 'column',
		gap: 6,
		minWidth: 0,
		paddingBottom: 12,

		'.mnBuySelOption': {
			fontSize: 15,
			fontWeight: 400,
			color: mono.ink,
		},
		'.mnBuySelUnit': {
			fontSize: 13,
			fontWeight: 300,
			color: mono.faint,
			fontVariantNumeric: 'tabular-nums',
		},
	},
	'.mnBuySelQtyField': {
		display: 'flex',
		flexDirection: 'column',
		gap: 8,
		'.mnBuySelQty': { height: 40 },
	},
	'.mnBuySelAmount': {
		fontSize: 18,
		fontWeight: 300,
		color: mono.ink,
		fontVariantNumeric: 'tabular-nums',
		paddingBottom: 10,
		whiteSpace: 'nowrap',
	},
	'.mnBuySelRemove': {
		padding: 0,
		paddingBottom: 12,
		border: 0,
		background: 'transparent',
		fontFamily: monoFont.sans,
		fontSize: 13,
		fontWeight: 500,
		color: mono.dim,
		cursor: 'pointer',
		transition: 'color 200ms ease-out',
		'&:hover': { color: mono.ink },
	},

	/** 폼 입력은 밑줄만. DESIGN_SYSTEM §5 */
	'.mnBuyQtyField': {
		maxWidth: 120,
	},
	'.mnBuyQty': {
		width: '100%',
		height: 46,
		padding: 0,
		border: 0,
		borderBottom: `1px solid ${mono.lineInput}`,
		borderRadius: 0,
		background: 'transparent',
		fontFamily: monoFont.sans,
		fontSize: 16,
		fontWeight: 300,
		color: mono.ink,
		outline: 'none',
		'&:focus': { borderBottomColor: mono.ink },
	},

	/*
		TOTAL — [라벨 | 금액] 2열 그리드. 착지 원가 줄이 둘째 행의 **금액 열**에 들어와
		숫자와 왼쪽 끝을 공유한다. flex 였을 때는 그 줄들이 패널 왼쪽 끝에서 시작해
		금액과 무관한 별개 블록으로 읽혔다 (2026-09-21).
	*/
	'.mnBuyTotal': {
		width: '100%',
		display: 'grid',
		gridTemplateColumns: 'auto minmax(0, 1fr)',
		columnGap: 18,
		alignItems: 'baseline',
		marginTop: 36,

		'.mnBuyTotalValue': {
			fontSize: 22,
			fontWeight: 300,
			color: mono.ink,
		},
	},

	/*
		착지 원가 — TOTAL 그리드의 둘째 행, 금액 열. **한 줄에 한 구**(`SHIPPING & VAT INCLUDED`)로,
		라벨과 값을 좌·우로 벌리지 않는다: 양 끝에 두면 눈이 두 번 움직이고 표처럼 읽힌다(2026-09-21).
		금액과 8px 만 띄운다 — 숫자에 딸린 캡션이지 다음 블록이 아니다.
		작고 굵은 키커라 DESIGN_SYSTEM §0 대로 — 크게 쓰는 것만 가늘게, 작게 쓰는 것은 굵게.
		헤어라인은 두지 않는다. 여기는 아직 구매 동작 안이라 선을 더하면 TOTAL 과 CTA 가 끊긴다.
	*/
	'.mnBuyLanded': {
		gridColumn: 2,
		alignSelf: 'start',
		minWidth: 0,
		marginTop: 8,
		display: 'flex',
		flexDirection: 'column',
		gap: 4,
	},
	'.mnBuyLandedLine': {
		margin: 0,
		fontSize: 11,
		lineHeight: 1.8,
		fontWeight: 500,
		letterSpacing: '.16em',
		textTransform: 'uppercase',
		color: mono.dim,
	},

	'.mnBuyAction': {
		width: '100%',
		marginTop: 32,

		'.mnBuyCta': {
			// 구매 화면의 주 동작이라 패널 폭을 쓴다 (일반 밴드의 인라인 CTA 와 다른 점)
			width: '100%',
			maxWidth: 380,

			'&:disabled': {
				background: mono.lineSoft,
				color: mono.faint,
				cursor: 'default',
			},
		},
	},

	/*
		값과 지표. DESIGN_SYSTEM §0 — **크기와 웨이트는 반비례한다.** 크게 쓰는 것은
		전부 가늘게(100~300), 작게 쓰는 라벨만 굵게(500)+대문자+자간. 그래야 큰 것이
		여럿이어도 시끄럽지 않다. 색으로 강조하지 않는다(액센트 없음).
	*/
	'.mnBuyLadder': {
		width: '100%',
		display: 'flex',
		flexDirection: 'column',
		gap: '1.5rem',
		paddingTop: '1.25rem',
		paddingBottom: '1.5rem',
		marginBottom: '0.5rem',
		borderBottom: '1px solid #E5E7EA',
	},

	/*
		값 계단 — 레일 한 줄 아래 상자 셋 (2026-09-21 사용자 결정). 점은 상자 가운데 위에 서고,
		지금 살 수 있는 칸은 **검정 바탕에 밝은 글씨**, 아직 오지 않은 칸은 **검정 테두리만**,
		지나간 칸은 물러선다. 레일과 상자 줄이 같은 폭 규칙(칸 flex 1 · 이음 `RAIL_GAP`)을 써서
		점과 상자 중심이 어긋나지 않고, 둘 다 패널 폭 100% 를 쓴다.

		채움색은 `ink` 이고 `ctaBg` 가 아니다. 페이지의 블랙 CTA 는 Add to cart 하나여야 하므로
		(DESIGN_SYSTEM §5) 누를 수 없는 이 표식은 CTA 와 같은 검정을 쓰지 않는다.
	*/
	'.mnBuySteps': {
		width: '100%',
		display: 'flex',
		flexDirection: 'column',
	},

	/* 레일 — 칸(flex 1) · 이음(고정폭). 아래 상자 줄과 같은 나눗셈이라 점이 상자 중심에 선다 */
	'.mnBuyStepRail': {
		width: '100%',
		display: 'flex',
		alignItems: 'center',
		height: 7,
		marginBottom: 12,
	},
	'.mnBuyStepTick': {
		flex: 1,
		minWidth: 0,
		display: 'flex',
		alignItems: 'center',
	},
	'.mnBuyRailGap': {
		flex: 'none',
		width: 14,
		height: 1,
		background: mono.line,
	},
	/* 마디 — 점 양옆의 반 토막. 끝(`off`)은 비우고, 못 사는 구간(`gap`)은 점선 */
	'.mnBuyRailLine': {
		flex: 1,
		height: 1,
		background: mono.line,
	},
	'.mnBuyRailLine.gap, .mnBuyRailGap.gap': {
		height: 0,
		background: 'none',
		borderTop: `1px dashed ${mono.lineSoft}`,
	},
	'.mnBuyRailLine.off': {
		background: 'none',
		borderTop: 0,
	},
	'.mnBuyStepDot': {
		flex: 'none',
		width: 7,
		height: 7,
		borderRadius: 999,
		border: `1px solid ${mono.lineSoft}`,
		background: mono.bg,

		// 지금 살 수 있는 값만 채운다
		'&.is-now': { background: mono.ink, borderColor: mono.ink },
		'&.is-past': { opacity: 0.4 },
	},

	/* 상자 줄 — 레일의 이음폭과 같은 간격 */
	'.mnBuyStepBoxes': {
		width: '100%',
		display: 'flex',
		alignItems: 'stretch',
		gap: 14,
	},
	'.mnBuyStep': {
		flex: 1,
		minWidth: 0,
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		textAlign: 'center',
		padding: '12px 10px 14px',
		border: `1px solid ${mono.line}`,
	},
	'.mnBuyStepLabel': {
		fontSize: '0.625rem',
		fontWeight: 500,
		letterSpacing: '0.1em',
		textTransform: 'uppercase',
		color: mono.faint,
		whiteSpace: 'nowrap',
	},
	'.mnBuyStepRange': {
		marginTop: 7,
		fontSize: '0.625rem',
		fontWeight: 500,
		letterSpacing: '0.06em',
		textTransform: 'uppercase',
		color: '#B4BAC1',
		fontVariantNumeric: 'tabular-nums',
		whiteSpace: 'nowrap',
	},
	'.mnBuyStepValue': {
		marginTop: 9,
		fontSize: '0.9375rem',
		fontWeight: 300,
		color: mono.dim,
		fontVariantNumeric: 'tabular-nums',
		whiteSpace: 'nowrap',
	},

	// 지금 — 검정 바탕. 이 화면에서 지금 낼 수 있는 값은 하나뿐이라 상자도 하나만 채워진다
	'.mnBuyStep.is-now': {
		background: mono.ink,
		borderColor: mono.ink,

		'.mnBuyStepLabel': { color: 'rgba(255, 255, 255, 0.62)' },
		'.mnBuyStepRange': { color: 'rgba(255, 255, 255, 0.46)' },
		'.mnBuyStepValue': { color: '#FFFFFF', fontWeight: 400 },
	},
	// 아직 — 테두리만. 올 값이라 글자는 읽히게 둔다
	'.mnBuyStep.is-next': {
		borderColor: mono.ink,

		'.mnBuyStepLabel': { color: mono.dim },
		'.mnBuyStepValue': { color: mono.ink },
	},
	// 지나간 — 자리는 지키되 꺼 둔다
	'.mnBuyStep.is-past': {
		borderColor: mono.line,
		opacity: 0.4,
	},

	/* 좁은 화면 — 상자 안 여백과 이음을 줄여 기간 줄(SEP 20 – SEP 27)이 잘리지 않게 한다 */
	'@media (max-width: 420px)': {
		'.mnBuyRailGap': { width: 8 },
		'.mnBuyStepBoxes': { gap: 8 },
		'.mnBuyStep': { padding: '10px 5px 12px' },
		'.mnBuyStepRange': { letterSpacing: '0.02em' },
	},

	/*
		시간과 수량. 값 계단과 같은 좌·우 끝을 쓴다 — 시간은 라벨 열(좌), 수량은 값 열(우).
		폭이 늘어도 두 지표가 가운데로 몰리거나 한쪽에 붙지 않는다.
		⚠ 클래스 이름은 `mnBuyFact*` 를 쓰지 않는다. ShopDetail 이 하단 스펙 표
		(LOOPS·DISPLAY·POWER)에 그 이름을 이미 쓰고 있고, 그쪽이 더 구체적이라 이긴다.
	*/
	'.mnBuyMeters': {
		width: '100%',
		display: 'grid',
		gridTemplateColumns: 'auto minmax(0, 1fr) auto',
		columnGap: '1.25rem',
		paddingTop: '1rem',
		borderTop: '1px solid #E5E7EA',
	},
	'.mnBuyMeter': {
		display: 'flex',
		flexDirection: 'column',
		gap: '0.25rem',
		minWidth: 0,
	},
	'.mnBuyMeterTime': {
		gridColumn: 1,
		alignItems: 'flex-start',
	},
	'.mnBuyMeterLimit': {
		gridColumn: 3,
		alignItems: 'flex-end',
		textAlign: 'right',
	},
	// 중첩을 풀어 둔다 — 부모 안에 넣으면 패널의 기존 span 규칙에 밀린다
	'.mnBuyMeterValue': {
		fontSize: '1.625rem',
		lineHeight: 1.1,
		fontWeight: 200,
		letterSpacing: '-0.01em',
		color: '#16181B',
		fontVariantNumeric: 'tabular-nums',
		whiteSpace: 'nowrap',
	},
	'.mnBuyMeterLabel': {
		fontSize: '0.625rem',
		fontWeight: 500,
		letterSpacing: '0.08em',
		lineHeight: 1.35,
		textTransform: 'uppercase',
		color: '#9AA1AA',
		whiteSpace: 'nowrap',
	},

	/*
		CTA 아래 조건 블록 — 라벨 하나가 문장 둘을 묶는다. 아래 스펙 행(`mnBuyFact`)과
		같은 [라벨 | 값] 문법이라 패널 하단이 한 리듬으로 내려간다.
	*/
	'.mnBuyTerms': {
		width: '100%',
		display: 'grid',
		gridTemplateColumns: 'auto minmax(0, 1fr)',
		columnGap: 20,
		marginTop: 26,
		paddingTop: 18,
		borderTop: `1px solid ${mono.line}`,

		'.mnBuyTermsLabel': {
			fontSize: 11,
			fontWeight: 500,
			letterSpacing: '.16em',
			textTransform: 'uppercase',
			color: mono.faint,
			lineHeight: 1.75,
			whiteSpace: 'nowrap',
		},
		'.mnBuyTermsLines': {
			display: 'flex',
			flexDirection: 'column',
			gap: 6,
			minWidth: 0,
		},
		'.mnBuyTermsLine': {
			margin: 0,
			fontSize: 14,
			lineHeight: 1.6,
			fontWeight: 300,
			color: mono.dim,
		},

		'@media (max-width: 560px)': {
			gridTemplateColumns: '1fr',
			rowGap: 10,
		},
	},

	'.mnBuyNotOpen': {
		paddingTop: '0.5rem',
	},
	'.mnBuyNote': {
		marginTop: 20,
		'&.mnBuyNoteFirst': { marginTop: 28 },
		'&.mnBuyNoteTight': { marginTop: 6 },
		fontSize: 15,
		lineHeight: 1.7,
		fontWeight: 300,
		color: mono.dim,
	},

	/*
		조건 문장 묶음 — 문장 사이를 좁혀 한 덩어리로 읽히게 한다.
		사다리의 다른 블록과는 따로 떨어져 있다(부모 gap).
	*/
	'.mnBuyLadderNotes': {
		width: '100%',
		display: 'flex',
		flexDirection: 'column',
		gap: '0.375rem',
	},

	/* 사다리 안에 서는 한 줄 — 표(13px)와 같은 눈높이라 값들과 한 묶음으로 읽힌다 */
	'.mnBuyLadderNote': {
		margin: 0,
		fontSize: 13,
		lineHeight: 1.65,
		fontWeight: 300,
		color: mono.dim,
	},

	'@media (prefers-reduced-motion: reduce)': {
		'.mnBuyChip': { transition: 'none' },
		'.mnBuySelRemove': { transition: 'none' },
	},
})
